import React, { useEffect, useState } from 'react'
import 'react-phone-number-input/style.css'
import { Country, getCountries, getCountryCallingCode } from 'react-phone-number-input'
import en from 'react-phone-number-input/locale/en.json'
import style from './PhoneInput.module.scss'
import { CustomCountries } from '../PhoneVerification/phone-verification.types'
import { CountrySelectModal } from '../PhoneVerification/country-selector'
import phoneVerificationStyles from '../PhoneVerification/phone-verification.module.scss'

interface PhoneInputProps {
  name: string
  value?: string
  onChange?: (phone: string, fullPhoneNumber: string) => void
  errorMessage?: string
  disabled?: boolean
  marginBottom?: boolean
  placeholder?: string
  label?: string
  required?: boolean
}

// Get all countries from the library
const countries: CustomCountries = getCountries().map(countryCode => ({
  code: countryCode,
  name: en[countryCode],
  dialCode: `+${getCountryCallingCode(countryCode)}`,
  flag: getCountryFlag(countryCode),
}))

// Helper function to get country flag emoji
function getCountryFlag(countryCode: Country): string {
  const codePoints = countryCode
    .toUpperCase()
    .split('')
    .map(char => 127397 + char.charCodeAt(0))
  return String.fromCodePoint(...codePoints)
}

const getCountryCodeDialCode = (countryCode: Country) => {
  return countries.find(c => c.code === countryCode)?.dialCode || '+1'
}

const PhoneInput: React.FC<PhoneInputProps> = ({
  name,
  value,
  onChange,
  errorMessage,
  disabled,
  marginBottom,
  placeholder,
  label,
  required,
}) => {
  const [isCountryModalOpen, setIsCountryModalOpen] = useState(false)
  const [selectedCountry, setSelectedCountry] = useState<Country>('GB')

  useEffect(() => {
    // Fetch user's country from IP
    fetch('https://ipapi.co/json/')
      .then(res => res.json())
      .then(data => {
        if (data.country_code) {
          setSelectedCountry(data.country_code as Country)
        }
      })
      .catch(() => {
        // Fallback to GB on error
        setSelectedCountry('GB')
      })
  }, [])

  const fullPhoneNumber = value ? `${getCountryCodeDialCode(selectedCountry)}${value}` : ''

  return (
    <div className={`${style.phoneInput} ${marginBottom ? style.marginBottom : ''}`}>
      {/* <PhoneInputLibrary
        international
        defaultCountry="GB"
        // value={value}
        onChange={e => {
          console.log(e)
        }}
        disabled={disabled}
        required={required}
        placeholder={placeholder}
        className={style.phoneInput}
      /> */}

      <div className={phoneVerificationStyles.phoneInputContainer}>
        {label && (
          <label className={style.phoneInputLabel}>
            {label}
            {required && <span className={style.phoneInputRequired}>*</span>}
          </label>
        )}
        <div className={phoneVerificationStyles.phoneInputWrapper}>
          <button
            type="button"
            className={phoneVerificationStyles.countrySelector}
            onClick={() => setIsCountryModalOpen(prev => !prev)}
          >
            <span className={phoneVerificationStyles.countryFlag}>
              {countries.find(c => c.code === selectedCountry)?.flag || '🇺🇸'}
            </span>
            <span className={phoneVerificationStyles.countryCode}>
              {countries.find(c => c.code === selectedCountry)?.dialCode || '+1'}
            </span>
            <span className={phoneVerificationStyles.dropdownArrow}>▼</span>
          </button>
          <input
            type="tel"
            name={name}
            className={phoneVerificationStyles.phoneNumberInput}
            placeholder={placeholder || 'Phone Number'}
            value={value || ''}
            disabled={disabled}
            onChange={e => {
              const value = e.target.value.replace(/\D/g, '')
              onChange?.(value, fullPhoneNumber)
            }}
            required={required}
          />
        </div>
        {errorMessage && (
          <span className={phoneVerificationStyles.errorMessage}>{errorMessage}</span>
        )}
      </div>

      {isCountryModalOpen && (
        <CountrySelectModal
          isOpen={isCountryModalOpen}
          countries={countries}
          onClose={() => setIsCountryModalOpen(false)}
          onSelect={country => {
            setSelectedCountry(country)
          }}
          selectedCountry={selectedCountry}
        />
      )}
    </div>
  )
}

export default PhoneInput
